import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { LinkOutlined } from "@ant-design/icons";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Button, Drawer, Image, List, Pagination, Skeleton, Tooltip, message } from "antd";
import { isEmpty, isUndefined } from "lodash";
import { useSelector } from "react-redux";
import {
  GET_LOCATION_VEHICLE_INVENTORIES_V2,
  GET_LOCATION_VEHICLE_INVENTORY,
} from "../../../graphql/query";
import { CREATE_VDP_LINK_INVENTORY } from "../../../graphql/mutation";
import { currencies } from "../../../library/constants";
import { formatPrice, getHighlightedText } from "../../../library/utils";
import useLang from "../../../hooks/useLang";
import "./vdpSelector.css";
import DebounceInput from "../../commonComponents/DebounceInput";
import VehicleMoreDetails from "./VDP/VehicleMoreDetails";
import VehicleInventoryDetailsDrawer from "../../inventory/VehicleInventoryDetailsDrawer";
import VdpLocationSearch from "./VdpLocationSearch";

// This is locationId of murdock hyundai location
const MURDOCK_LOCATION_ID = 448;

// Reusable DetailRow component
const DetailRow = ({ label, value }) => (
  <div className="flex gap-3 text-sm font-normal">
    <span className="text-black/[0.65] w-[4.5rem] flex-shrink-0 text-right">{label}</span>
    <span className="flex-1 text-black/[0.88] break-words"> : {value}</span>
  </div>
);

const VDPSelector = forwardRef((props, ref) => {
  const [lang] = useLang();
  const {
    text,
    setmessageText,
    handleInsertLink = () => {},
    onClose,
    onSelectedVehiclesChange,
  } = props;
  const { textMode } = useSelector(state => state.lead);
  const [selectedVehicle, setselectedVehicle] = useState([]);
  const [search, setsearch] = useState("");
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50 });
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [moreDetailsDrawerOpen, setMoreDetailsDrawerOpen] = useState(false);
  const [selectedVehicleForDetails, setSelectedVehicleForDetails] = useState(null);
  const [inventoryDetailsDrawerOpen, setInventoryDetailsDrawerOpen] = useState(false);
  const [selectedVehicleForInventoryDetails, setSelectedVehicleForInventoryDetails] =
    useState(null);

  const currentLocationId = +sessionStorage.getItem("currentLocationId");
  const isMurdockLocation = currentLocationId === MURDOCK_LOCATION_ID;

  const [getVehicleInventory, { data: vdpData, loading: vdpLoader }] = useLazyQuery(
    GET_LOCATION_VEHICLE_INVENTORY,
    {
      fetchPolicy: "cache-and-network",
    }
  );

  const [getVehicleInventoriesV2, { data: vdpDataV2, loading: vdpLoaderV2 }] = useLazyQuery(
    GET_LOCATION_VEHICLE_INVENTORIES_V2,
    {
      fetchPolicy: "network-only",
    }
  );

  useEffect(() => {
    if (selectedLocations?.length > 0) {
      // Use V2 API only for location 448
      if (isMurdockLocation) {
        getVehicleInventoriesV2({
          variables: {
            locationIds: selectedLocations
              .filter(item => item.isChecked && +item.id !== 0)
              .map(item => item.id),
            search,
            page: pagination.page,
            pageSize: pagination.pageSize,
          },
        });
      } else {
        // Use old API for other locations
        getVehicleInventory({
          variables: {
            currentLocationId: currentLocationId,
            locationIds: selectedLocations
              .filter(item => item.isChecked && +item.id !== 0)
              .map(item => item.id),
            isAllLocations: false,
            search,
            page: pagination.page,
            pageSize: pagination.pageSize,
          },
        });
      }
    }
  }, [selectedLocations, search, pagination]);

  const handleAddKeyword = keyword => {
    if (textMode === "Email") {
      handleInsertLink(keyword);
    } else {
      const ctl = document.getElementById("text");
      const currentValue = text.current.value;
      // Fall back to the end of the text when the textarea isn't focused, so the
      // link is appended instead of inserted at position 0.
      const startPos = ctl.selectionStart != null ? ctl.selectionStart : currentValue.length;

      const before = currentValue.slice(0, startPos);
      const after = currentValue.slice(startPos);

      // Ensure the inserted link is separated by a space from the surrounding
      // text so multiple shorten URLs don't get concatenated together.
      const needsLeadingSpace = before.length > 0 && !/\s$/.test(before);
      const needsTrailingSpace = after.length === 0 || !/^\s/.test(after);
      const insertText = `${needsLeadingSpace ? " " : ""}${keyword}${
        needsTrailingSpace ? " " : ""
      }`;

      text.current.value = [before, insertText, after].join("");
      if (text.current) {
        // Place the cursor right after the inserted link (and its trailing space).
        const newCursorPos = before.length + insertText.length;
        text.current.focus();
        text.current.setSelectionRange(newCursorPos, newCursorPos);
        text.current.style.height = "inherit";
        text.current.style.height = `${text.current.scrollHeight}px`;
      }
      setmessageText(text.current.value);
    }
  };

  const [createVdpLinks, { loading: vdpLinkLoader }] = useMutation(CREATE_VDP_LINK_INVENTORY);

  const parseLink = async () => {
    const res = await createVdpLinks({
      variables: {
        vehicleIds: selectedVehicle.map(item => +item),
      },
    });
    if (res.data.createVdpLinksVehicleInventory.statusCode === 200) {
      message.success(res?.data?.createVdpLinksVehicleInventory?.message);
      const fullString = res?.data?.createVdpLinksVehicleInventory?.vdpLinks.join("\n");
      return fullString;
    }
    message.error(res?.data?.createVdpLinksVehicleInventory?.message);
  };

  // Notify parent about selected vehicles changes
  useEffect(() => {
    onSelectedVehiclesChange && onSelectedVehiclesChange(selectedVehicle);
  }, [selectedVehicle, onSelectedVehiclesChange]);

  useImperativeHandle(ref, () => ({
    getSelectedVehicles: () => selectedVehicle,
    addVehicles: async () => {
      handleAddKeyword(await parseLink());
      onClose && onClose();
    },
  }));

  const getVinLink = (item, vdpUrl, isNew) => {
    let inventoryLink = "";
    if (vdpUrl && !isEmpty(vdpUrl)) {
      inventoryLink = vdpUrl;
    } else if (
      item?.location?.inventoryUrlFormat &&
      item?.location?.inventoryUrlFormat !== "" &&
      item?.location?.inventoryUrlFormat !== null
    ) {
      const url = item?.location?.inventoryUrlFormat || "";
      const isNewOrUsed = isNew ? "new" : "used";
      inventoryLink = url
        .replace("{vinNumber}", item?.vin)
        .replace("{vehicleUsedOrNew}", isNewOrUsed)
        .replace("{vehicleMake}", item?.make);
    }
    return inventoryLink;
  };

  const formatLocations = locations => {
    const checkedLocations = locations.filter(item => item.isChecked);
    if (checkedLocations.length <= 2) {
      return checkedLocations.map(item => item.title).join(", ");
    }
    const displayedLocations = checkedLocations
      .slice(0, 2)
      .map(item => item.title)
      .join(", ");
    return `${displayedLocations} and +${checkedLocations.length - 2} more`;
  };

  // Determine which data to use based on location
  const count = isMurdockLocation
    ? vdpDataV2?.getVehicleInventoriesV2?.count || 0
    : vdpData?.getVehicleInventoryV2?.count || 0;

  const vehicleList = isMurdockLocation
    ? vdpDataV2?.getVehicleInventoriesV2?.vehicleInventories || []
    : vdpData?.getVehicleInventoryV2?.data || [];

  const isLoading = isMurdockLocation ? vdpLoaderV2 : vdpLoader;

  const paginationProps = {
    current: pagination.page,
    pageSize: pagination.pageSize,
    total: count || 0,
    showTotal: total => `${lang.total}: ${total} ${lang.vehicles}`,
    pageSizeOptions: ["25", "50", "100", "150", "200", "500"],
    showSizeChanger: true,
    onChange: (page, pageSize) => {
      setPagination({ page, pageSize });
    },
    onShowSizeChange: (current, size) => {
      setPagination({ page: current, pageSize: size });
    },
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center space-x-2 p-2">
        <VdpLocationSearch onChange={setSelectedLocations} onLoadingChange={setLocationLoading} />

        <DebounceInput
          className="w-full"
          placeholder="Search by vehicle name"
          suffix={
            <svg xmlns="http://www.w3.org/2000/svg" width={18} height={18} fill="none">
              <path
                fill="#64748B"
                stroke="#94A3B8"
                strokeWidth={0.078}
                d="m13 12.122-.022.027.025.026 3.912 3.91a.585.585 0 1 1-.83.83l-3.91-3.912-.026-.025-.027.023a6.844 6.844 0 1 1 .879-.879ZM2.087 7.75a5.67 5.67 0 0 0 5.664 5.664A5.664 5.664 0 1 0 2.086 7.75Z"
              />
            </svg>
          }
          value={search}
          onChange={value => {
            setsearch(value);
            setPagination(prev => ({ ...prev, page: 1 }));
          }}
        />
      </div>
      <div className="px-2 pb-2">
        <span className="text-black/[0.65] text-sm font-normal">
          Showing results for:{" "}
          <span className="text-black/[0.88]">
            {selectedLocations?.length > 1 &&
            selectedLocations?.every(item => item.isChecked === true)
              ? "All Locations"
              : formatLocations(selectedLocations)}
          </span>
        </span>
      </div>
      <List
        loading={isLoading || locationLoading}
        className="mt-2 flex-1 overflow-y-auto vdp-scrollbar-hide"
        dataSource={vehicleList}
        renderItem={(item, index) => {
          // Normalize data structure based on location
          const vehicleId = isMurdockLocation ? item.vehicle_id : item.id;
          const photos = isMurdockLocation
            ? (item?.list_of_photos || []).map(photo => photo.PhotoUrl)
            : (item?.vehicleInventoryMedias || []).map(media => media.mediaUrl);
          const photoSrc = isMurdockLocation
            ? (item?.list_of_photos || [])[0]?.PhotoUrl
            : (item?.vehicleInventoryMedias || [])[0]?.mediaUrl;
          const sellingPrice = isMurdockLocation ? item?.selling_price : item?.sellingPrice;
          const currencyUnit = isMurdockLocation ? item?.currency_unit : item?.currencyUnit;
          const locationId = isMurdockLocation ? item?.location_id : item?.locationId;
          const stockNumber = isMurdockLocation ? item?.stock_number : item?.stockNumber;
          const modelNumber = isMurdockLocation ? item?.model_number : item?.modelNumber;
          const exteriorColor = isMurdockLocation ? item?.exterior_color : item?.exteriorColor;
          const interiorColor = isMurdockLocation ? item?.interior_color : item?.interiorColor;
          const vdpUrl = isMurdockLocation ? item?.vdp_url : item?.vdpUrl;
          const isNew = isMurdockLocation ? item?.is_new : item?.isNew;

          return (
            <div
              key={index}
              onClick={() =>
                setselectedVehicle(prevSelected => {
                  // Check if the maximum selection limit (3) has been reached
                  if (prevSelected.length >= 3) {
                    // If the limit is reached and the item is already selected, remove it
                    if (prevSelected.includes(vehicleId)) {
                      return prevSelected.filter(id => id !== vehicleId);
                    }
                    // If the limit is reached and the item is not selected, return the current selection
                    return prevSelected;
                  }
                  // If the maximum selection limit has not been reached, toggle the item as before
                  if (prevSelected.includes(vehicleId)) {
                    return prevSelected.filter(id => id !== vehicleId);
                  }
                  return [...prevSelected, vehicleId];
                })
              }
              className={`flex flex-col p-4 gap-3 mb-4 cursor-pointer border rounded-xl border-solid ${
                selectedVehicle.includes(vehicleId)
                  ? "border-[#4096FF] bg-[#E6F4FF]"
                  : "border-[#D9D9D9]"
              }`}
            >
              {/* Container 1 */}
              <div className="flex flex-row gap-3">
                <Image.PreviewGroup items={photos}>
                  <Image
                    onClick={e => e.stopPropagation()}
                    placeholder={
                      <Skeleton.Node style={{ width: 100, height: 70, borderRadius: 15 }} active />
                    }
                    fallback="https://funnel-webapp-uploads.s3.amazonaws.com/prod/images/generic-car-not-available-image.png"
                    className="h-[70px] object-cover w-[100px] rounded-[15px]"
                    src={photoSrc || ""}
                    alt="Vehicle"
                  />
                </Image.PreviewGroup>
                <div className="flex flex-col gap-1 w-full">
                  <div className="w-full flex justify-between items-start">
                    <span className="text-base text-black/[0.88] font-bold flex-1">
                      {getHighlightedText(
                        `${item?.year === 0 || isUndefined(item?.year) ? "" : item?.year} ${
                          isEmpty(item?.make) ? "" : item?.make
                        } ${isEmpty(item?.model) ? "" : item?.model} ${
                          isEmpty(item?.trim) ? "" : item?.trim
                        }`,
                        search,
                        "truncate block"
                      )}
                    </span>
                    <span
                      className="text-[#1677FF] text-sm cursor-pointer hover:underline ml-2 flex-shrink-0"
                      onClick={e => {
                        e.stopPropagation();
                        // Normalize data for VehicleInventoryDetailsDrawer
                        const normalizedVehicle = isMurdockLocation
                          ? {
                              id: item.vehicle_id,
                              locationId: item.location_id,
                              make: item.make,
                              model: item.model,
                              year: item.year,
                              trim: item.trim,
                              stockNumber: item.stock_number,
                              modelNumber: item.model_number,
                              vin: item.vin,
                              msrp: item.msrp,
                              sellingPrice: item.selling_price,
                              currencyUnit: item.currency_unit,
                              isNew: item.is_new,
                              exteriorColor: item.exterior_color,
                              interiorColor: item.interior_color,
                              vdpUrl: item.vdp_url,
                              vehicleInventoryMedias: (item.list_of_photos || []).map(photo => ({
                                mediaUrl: photo.PhotoUrl,
                              })),
                            }
                          : item;
                        setSelectedVehicleForInventoryDetails(normalizedVehicle);
                        setInventoryDetailsDrawerOpen(true);
                      }}
                    >
                      View
                    </span>
                  </div>
                  <span className="text-sm font-normal text-black/[0.88]">
                    <span>MSRP</span>{" "}
                    {item?.msrp || sellingPrice
                      ? `${currencies?.find(currency => currency.code === currencyUnit)?.symbol} ${
                          formatPrice(item?.msrp || sellingPrice) || "N/A"
                        }`
                      : "Price not available"}
                    <Tooltip title="Open inventory">
                      <LinkOutlined
                        onClick={e => {
                          e.stopPropagation();
                          window.open(getVinLink(item, vdpUrl, isNew));
                        }}
                        className="text-blue-500 ml-2 mt-1"
                      />
                    </Tooltip>
                  </span>
                  <div className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width={10} height={14} fill="none">
                      <path
                        fill="#334155"
                        d="M5 3.5a2.187 2.187 0 1 0 0 4.375A2.187 2.187 0 0 0 5 3.5ZM5 7a1.313 1.313 0 1 1 0-2.625A1.313 1.313 0 0 1 5 7ZM5 .875A4.818 4.818 0 0 0 .187 5.688c0 1.717.794 3.537 2.297 5.263.676.78 1.436 1.483 2.267 2.095a.438.438 0 0 0 .502 0 13.9 13.9 0 0 0 2.263-2.095c1.5-1.726 2.296-3.546 2.296-5.264A4.818 4.818 0 0 0 5 .875Zm0 11.266c-.904-.711-3.938-3.323-3.938-6.454a3.937 3.937 0 1 1 7.875 0c0 3.13-3.033 5.743-3.937 6.454Z"
                      />
                    </svg>
                    <span className="ml-2 text-xs font-normal text-black/[0.65]">
                      {selectedLocations?.map(data => {
                        if (+data?.id === +locationId) {
                          return data?.title;
                        }
                      })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Container 2 */}
              <div
                className={`${
                  selectedVehicle.includes(vehicleId) ? "bg-white" : "bg-[#F5F5F5]"
                } rounded-xl p-4 flex flex-col font-[400] gap-1`}
              >
                <DetailRow label="VIN" value={getHighlightedText(item?.vin || "VIN N/A", search)} />
                <DetailRow
                  label="Stock No"
                  value={getHighlightedText(stockNumber || "N/A", search)}
                />
                <DetailRow
                  label="Model No"
                  value={getHighlightedText(modelNumber || "N/A", search)}
                />
                <DetailRow
                  label="Color"
                  value={
                    <>
                      {getHighlightedText(exteriorColor || "N/A", search)} (E)
                      {", "}
                      {getHighlightedText(interiorColor || "N/A", search)} (I)
                    </>
                  }
                />
              </div>

              {isMurdockLocation && (
                <Button
                  type="link"
                  className="!p-0 !w-fit !text-[#1677FF]"
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedVehicleForDetails(item);
                    setMoreDetailsDrawerOpen(true);
                  }}
                >
                  View more details
                </Button>
              )}
            </div>
          );
        }}
        pagination={null}
        itemLayout="horizontal"
      />
      <div className="sticky bottom-0 z-10 bg-white border-t border-gray-200 pt-2 mt-auto">
        <Pagination size="small" {...paginationProps} />
      </div>

      {moreDetailsDrawerOpen && (
        <Drawer
          title="More information"
          placement="right"
          width={600}
          onClose={() => {
            setMoreDetailsDrawerOpen(false);
            setSelectedVehicleForDetails(null);
          }}
          open={moreDetailsDrawerOpen}
        >
          <VehicleMoreDetails vehicle={selectedVehicleForDetails} />
        </Drawer>
      )}

      <VehicleInventoryDetailsDrawer
        open={inventoryDetailsDrawerOpen}
        onClose={() => {
          setInventoryDetailsDrawerOpen(false);
          setSelectedVehicleForInventoryDetails(null);
        }}
        vehicleData={selectedVehicleForInventoryDetails}
      />

      <style jsx>{`
        /* For Webkit-based browsers (Chrome, Safari and Opera) */
        .vdp-scrollbar-hide::-webkit-scrollbar {
          display: none;
        }

        /* For IE, Edge and Firefox */
        .vdp-scrollbar-hide {
          -ms-overflow-style: none; /* IE and Edge */
          scrollbar-width: none; /* Firefox */
        }
      `}</style>
    </div>
  );
});

VDPSelector.displayName = "VDPSelector";

export default VDPSelector;
